/**
 * Betty's tasks, laid out for the owner: the weekly reviews report (her take, four numbers, the
 * patient's path from visit to Google review, quick checks) and a setup step's go-live tracker.
 * Everything is computed from the figures of the reads the task cites, never from her text. A new
 * Google review is always called an estimate: Google doesn't say who wrote it.
 */

import type { GoLiveData, ReviewsFunnelRungs, ReviewsReportData } from '../types';

/** Betty's proposals with their own layout: a front desk routine, and a step towards going live. */
export const BETTY_PROPOSAL_SKILLS: ReadonlySet<string> = new Set(['front_desk_routine_coach', 'review_program_setup']);

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export interface ReviewsTake {
  sentences: string;
  tiles: Array<{ label: string; value: string; note: string }>;
}

/** Betty's take: plain sentences and four numbers, each with last week beside it when known. */
export function reviewsTake(report: ReviewsReportData, agentName = 'Betty'): ReviewsTake {
  const { now, previous, roster } = report;
  const parts: string[] = [];
  if (now.sent === 0) {
    parts.push('No review texts went out this week.');
    if (roster && roster.businessDays > 0 && roster.daysWithUpload < roster.businessDays) {
      parts.push(`The visit list was uploaded on ${roster.daysWithUpload} of ${plural(roster.businessDays, 'business day', 'business days')}, and ${agentName} texts patients from that list.`);
    }
  } else {
    const replied = now.replied === 0
      ? 'no patient has replied yet'
      : `${plural(now.replied, 'patient', 'patients')} replied${now.rated5 > 0 ? `, ${now.rated5 === now.replied ? (now.replied === 1 ? 'rating the visit 5 out of 5' : 'all rating the visit 5 out of 5') : `${now.rated5} of them with 5 out of 5`}` : ''}`;
    parts.push(`${plural(now.sent, 'review text', 'review texts')} went out this week and ${replied}.`);
  }
  parts.push(now.matchedReviews > 0
    ? `${agentName} matched ${plural(now.matchedReviews, 'new Google review', 'new Google reviews')} to patients who got a text (an estimate).`
    : 'No new Google reviews were matched yet.');

  const last = (n: number | undefined) => (n === undefined ? 'this week' : `${n} last week`);
  return {
    sentences: parts.join(' '),
    tiles: [
      { label: 'Visits confirmed', value: String(now.confirmedVisits), note: last(previous?.confirmedVisits) },
      { label: 'Review texts sent', value: String(now.sent), note: last(previous?.sent) },
      { label: 'Patients who replied', value: String(now.replied), note: now.rated5 > 0 ? `${now.rated5} rated 5 out of 5` : last(previous?.replied) },
      { label: 'New Google reviews', value: String(now.matchedReviews), note: previous ? `estimate · ${previous.matchedReviews} last week` : 'estimate' },
    ],
  };
}

const RUNGS: Array<{ key: keyof ReviewsFunnelRungs; label: string; note?: string }> = [
  { key: 'confirmedVisits', label: 'Visits confirmed' },
  { key: 'sent', label: 'Review texts sent' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'replied', label: 'Patient replied' },
  { key: 'rated5', label: 'Rated 5 out of 5' },
  { key: 'draftApproved', label: 'Approved their review draft', note: 'the patient’s step' },
  { key: 'linkTapped', label: 'Tapped the Google link' },
  { key: 'matchedReviews', label: 'Google review matched', note: 'estimate' },
];

export interface FunnelRow {
  label: string;
  value: number;
  note?: string;
  /** 0-100, relative to this week's and last week's largest rung, so the two weeks compare. */
  width: number;
}

export function funnelRows(report: ReviewsReportData, week: 'now' | 'previous'): FunnelRow[] {
  const rungs = week === 'now' ? report.now : report.previous;
  if (!rungs) return [];
  const max = Math.max(1, ...RUNGS.map((r) => Math.max(report.now[r.key], report.previous?.[r.key] ?? 0)));
  return RUNGS.map((r) => ({
    label: r.label,
    value: rungs[r.key],
    ...(r.note ? { note: r.note } : {}),
    width: rungs[r.key] > 0 ? Math.max(4, Math.round((rungs[r.key] / max) * 100)) : 0,
  }));
}

/** Rates mean little on a handful of texts; Betty compares week to week from this many sends. */
export const MIN_SENDS_TO_COMPARE = 30;

export interface ReviewsCheck {
  title: string;
  body: string;
  /** ok: nothing needs the owner; attention: worth a look; info: neither. */
  tone: 'ok' | 'attention' | 'info';
}

/** Quick checks, each only when its read was made. */
export function reviewsChecks(report: ReviewsReportData): ReviewsCheck[] {
  const checks: ReviewsCheck[] = [];
  const { roster } = report;
  if (roster && roster.businessDays > 0) {
    const allUp = roster.daysWithUpload === roster.businessDays;
    const confirmed = roster.visitsFinished > 0 ? ` ${roster.confirmedSameDay} of ${plural(roster.visitsFinished, 'visit', 'visits')} confirmed the same day.` : '';
    checks.push({
      title: 'Front desk',
      body: `Visit list uploaded on ${roster.daysWithUpload} of ${plural(roster.businessDays, 'business day', 'business days')}.${confirmed}`,
      tone: allUp ? 'ok' : 'attention',
    });
  }
  if (report.privateFeedbackOpen !== null) {
    checks.push(report.privateFeedbackOpen === 0
      ? { title: 'Unhappy patients', body: 'Nobody is waiting for a call back.', tone: 'ok' }
      : { title: 'Unhappy patients', body: `${plural(report.privateFeedbackOpen, 'patient is', 'patients are')} waiting for a call back.`, tone: 'attention' });
  }
  if (report.failedTexts !== null) {
    checks.push(report.failedTexts === 0
      ? { title: 'Failed texts', body: 'None this week.', tone: 'ok' }
      : { title: 'Failed texts', body: `${plural(report.failedTexts, 'text', 'texts')} didn't reach the patient.`, tone: 'attention' });
  }
  if (report.qr) {
    checks.push({ title: 'QR code at the desk', body: `${plural(report.qr.scans, 'scan', 'scans')} this week${report.qr.matchedReviews > 0 ? `, ${plural(report.qr.matchedReviews, 'review', 'reviews')} matched` : ''}.`, tone: 'info' });
  }
  return checks;
}

export type GoLiveStepState = 'done' | 'current' | 'todo' | 'unknown';

export interface GoLiveStepView {
  key: GoLiveData['steps'][number]['key'];
  label: string;
  state: GoLiveStepState;
}

/**
 * The go-live steps with the first one not done marked current (the one this task is about). A
 * step the reads don't cover shows as not checked, and is never counted as done.
 */
export function goLiveSteps(goLive: GoLiveData): { steps: GoLiveStepView[]; done: number; total: number } {
  let currentSet = false;
  const steps = goLive.steps.map((step): GoLiveStepView => {
    if (step.done === true) return { key: step.key, label: step.label, state: 'done' };
    if (!currentSet && step.done === false) {
      currentSet = true;
      return { key: step.key, label: step.label, state: 'current' };
    }
    return { key: step.key, label: step.label, state: step.done === null ? 'unknown' : 'todo' };
  });
  return { steps, done: steps.filter((s) => s.state === 'done').length, total: steps.length };
}
